/**
 * Re-price cart lines from the product list — the client half of 090.
 *
 * A cart line snapshots the product's price when it is added, and until
 * 2026-09-15 that snapshot was what the sale was booked at: a till whose
 * product cache predated the owner's price change sold at the old price,
 * and a held cart resumed the next day did the same. create_sale now
 * refuses a line whose price is not the product's (PRICE_MISMATCH), and the
 * till keeps its lines honest with this: whenever the product list changes
 * — a periodic refresh, a refresh after a refused sale — every line takes
 * the product's current list and wholesale price.
 *
 * Returns the SAME array when nothing changed, so a React effect can skip
 * the state update (and its re-render) on the common path.
 */
import type { T } from './i18n'

export type RepriceLine = { product_id: string; product_name?: string; unit_price: number; wholesale_price?: number | null }
export type RepriceProduct = { id: string; sale_price: number; wholesale_price?: number | null }
export type PriceChange = { product_id: string; name: string; from: number; to: number }

const same = (a: number, b: number) => Math.abs(a - b) < 0.005
const sameNullable = (a: number | null | undefined, b: number | null | undefined) =>
  (a == null && b == null) || (a != null && b != null && same(a, b))

export function repriceLines<T extends RepriceLine>(
  lines: T[],
  products: RepriceProduct[],
): { lines: T[]; changes: PriceChange[] } {
  if (lines.length === 0) return { lines, changes: [] }
  const byId = new Map(products.map((p) => [p.id, p]))
  const changes: PriceChange[] = []
  let touched = false
  const next = lines.map((l) => {
    const p = byId.get(l.product_id)
    // A line whose product is gone stays as it is — the server will say so.
    if (!p) return l
    if (same(l.unit_price, p.sale_price) && sameNullable(l.wholesale_price, p.wholesale_price)) return l
    touched = true
    if (!same(l.unit_price, p.sale_price)) {
      changes.push({ product_id: l.product_id, name: l.product_name ?? '', from: l.unit_price, to: p.sale_price })
    }
    return { ...l, unit_price: p.sale_price, wholesale_price: p.wholesale_price ?? null }
  })
  return { lines: touched ? next : lines, changes }
}

/** One line for the toast: „„კოკა-კოლა" ₾5.00 → ₾5.50" (+ „და კიდევ N"),
 *  in the language of the `t` it is given (the till's). */
export function describePriceChanges(changes: PriceChange[], t: T): string {
  if (changes.length === 0) return ''
  const [c, ...rest] = changes
  const head = t('reprice.line', { name: c.name || t('common.product'), from: `₾${c.from.toFixed(2)}`, to: `₾${c.to.toFixed(2)}` })
  return rest.length > 0 ? t('reprice.more', { head, n: rest.length }) : head
}

/**
 * The server's refusal, parsed: `PRICE_MISMATCH:<name>:<sent>:<list>`. A
 * product name may itself contain „:", so the two numbers are read from
 * the end and the rest is the name.
 */
export function parsePriceMismatch(message: string): { name: string; sent: number; list: number } | null {
  const i = message.indexOf('PRICE_MISMATCH:')
  if (i < 0) return null
  const parts = message.slice(i + 'PRICE_MISMATCH:'.length).split(':')
  if (parts.length < 3) return null
  const list = Number(parts.pop())
  const sent = Number(parts.pop())
  const name = parts.join(':')
  if (!Number.isFinite(list) || !Number.isFinite(sent)) return null
  return { name, sent, list }
}

/**
 * 1.58 — what a PRICE_MISMATCH refreshes before the cart re-prices: the
 * products (the owner changed a price), the price groups (a group's rule or
 * switch) and the customers (who is in which group) — each changed on another
 * device is a reason the server refused the line.
 */
export const PRICE_REFRESH_TABLES = ['products', 'price_groups', 'customers'] as const
